/* Custom records — handover §14 step 9, the dynamic renderer half.
 *
 * One screen for every record type there will ever be. The columns are the
 * fields flagged `list`, the form is every field, and both come out of the type
 * definition — so a type somebody adds next year gets a working register with
 * no code.
 *
 * The route carries the type id, and optionally a record id, because the expiry
 * aggregation links straight to /records/:typeId/:id. An expiry you cannot
 * click through to is a reminder without an action.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useOnline } from '../../hooks/useOnline';
import { expiryTone, fmtDate } from '../../lib/format';
import type { ICustomRecord, IRecordType } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  Drawer,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  TableSkeleton,
  type Column,
} from '../ui';
import { FieldInput, missingRequired, renderValue, type FieldLookups } from './RecordField';
import s from './screens.module.scss';

export function RecordsScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();
  const { typeId, id } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<ICustomRecord | null>(null);
  const [payload, setPayload] = useState<Record<string, unknown>>({});

  const types = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  const records = useQuery({
    queryKey: qk.customRecords(typeId),
    queryFn: () => svc.recordTypes.records(typeId),
  });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });

  const type: IRecordType | undefined = (types.data ?? []).find((t) => t.id === typeId);
  const lookups: FieldLookups = { people: people.data ?? [], suppliers: suppliers.data ?? [] };

  const save = useMutation({

    meta: { ok: 'Record saved' },
    mutationFn: (record: ICustomRecord) => svc.recordTypes.saveRecord(record),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.customRecords(typeId) });
      void qc.invalidateQueries({ queryKey: qk.customRecords() });
      void qc.invalidateQueries({ queryKey: qk.audit });
      close();
    },
  });

  const remove = useMutation({

    meta: { ok: 'Record deleted' },
    mutationFn: (recordId: string) => svc.recordTypes.removeRecord(recordId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.customRecords(typeId) });
      void qc.invalidateQueries({ queryKey: qk.customRecords() });
      close();
    },
  });

  /* A record id in the path opens that record — this is the expiry deep link
     landing. Runs when the data arrives, not just on mount, because the link is
     usually followed before the query has resolved. */
  useEffect(() => {
    if (!id || editing) return;
    const found = (records.data ?? []).find((r) => r.id === id);
    if (found) {
      setEditing(found);
      setPayload({ ...found.payload });
    }
  }, [id, records.data, editing]);

  function close() {
    setEditing(null);
    setPayload({});
    if (id) navigate(`/records/${typeId}`, { replace: true });
  }

  function openNew() {
    if (!type) return;
    setEditing({ id: '', title: '', typeId: type.id, payload: {} });
    setPayload({});
  }

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return records.data ?? [];
    return (records.data ?? []).filter(
      (r) =>
        r.title.toLowerCase().includes(q) ||
        Object.values(r.payload).some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
  }, [records.data, query]);

  const listFields = (type?.fields ?? []).filter((f) => f.list);

  const columns: Column<ICustomRecord>[] = listFields.map((field, i) => ({
    key: field.id,
    header: field.label,
    sortBy: (r) => {
      const v = r.payload[field.id];
      return typeof v === 'number' ? v : String(v ?? '');
    },
    render: (r) =>
      /* The first listed field carries the icon, so a row reads as a thing
         rather than as a spreadsheet line. */
      i === 0 ? (
        <div className={s.person}>
          <span className={s.chip}>
            <i className={`ti ${type?.icon ?? 'ti-file-text'}`} aria-hidden />
          </span>
          <div>
            <div className={s.cellMain}>{renderValue(field, r.payload[field.id], lookups)}</div>
            {r.expiryDate && (
              <div className={s.cellSub}>Expires {fmtDate(r.expiryDate)}</div>
            )}
          </div>
        </div>
      ) : field.expiry ? (
        <Badge tone={expiryTone(String(r.payload[field.id] ?? ''))}>
          {renderValue(field, r.payload[field.id], lookups)}
        </Badge>
      ) : (
        renderValue(field, r.payload[field.id], lookups)
      ),
  }));

  if (types.isLoading) {
    return (
      <Panel flush>
        <TableSkeleton />
      </Panel>
    );
  }

  if (!type) {
    return (
      <>
        <div className={s.head}>
          <div className={s.grow}>
            <h1>Records</h1>
          </div>
        </div>
        <Panel flush>
          <EmptyState icon="ti-layout-grid-add" title="No such record type">
            This type has been deleted, or the link is wrong. Record types are managed under Record
            types.
          </EmptyState>
        </Panel>
      </>
    );
  }

  const missing = missingRequired(type.fields, payload);

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>{type.title}</h1>
          <p>{type.description}</p>
        </div>
        <Button primary icon="ti-plus" onClick={openNew} disabled={!online}>
          New {type.title.toLowerCase()}
        </Button>
      </div>

      {records.isError && <LoadError error={records.error} what={type.title.toLowerCase()} />}

      <Controls count={`${rows.length} of ${records.data?.length ?? 0}`}>
        <SearchBox value={query} onChange={setQuery} placeholder={`Search ${type.title.toLowerCase()}…`} />
      </Controls>

      <Panel flush>
        {records.isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(r) => r.id}
            onRowClick={(r) => {
              setEditing(r);
              setPayload({ ...r.payload });
            }}
            empty={
              <EmptyState icon={type.icon} title={`No ${type.title.toLowerCase()} yet`}>
                {type.description}
              </EmptyState>
            }
          />
        )}
      </Panel>

      <Drawer
        open={editing !== null}
        title={editing?.id ? editing.title : `New ${type.title.toLowerCase()}`}
        sub={type.title}
        avatar={<i className={`ti ${type.icon}`} aria-hidden />}
        meta={
          editing?.id ? (
            <>
              {editing.status && <Badge tone="muted">{editing.status}</Badge>}
              {editing.expiryDate && (
                <Badge tone={expiryTone(editing.expiryDate)}>
                  Expires {fmtDate(editing.expiryDate)}
                </Badge>
              )}
            </>
          ) : undefined
        }
        onClose={close}
      >
        {editing && (
          <>
            {type.fields.map((field) => (
              <FieldInput
                key={field.id}
                field={field}
                value={payload[field.id]}
                onChange={(v) => setPayload((p) => ({ ...p, [field.id]: v }))}
                lookups={lookups}
              />
            ))}

            {missing.length > 0 && (
              <div className={s.ownedNote}>
                <i className="ti ti-alert-triangle" aria-hidden />
                <span>
                  {missing.map((f) => f.label).join(' and ')}{' '}
                  {missing.length === 1 ? 'is' : 'are'} required.
                </span>
              </div>
            )}

            <div className={s.actions}>
              <Button
                primary
                icon="ti-device-floppy"
                disabled={!online || save.isPending || missing.length > 0}
                onClick={() => save.mutate({ ...editing, payload })}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              {editing.id && (
                <Button
                  icon="ti-trash"
                  disabled={!online || remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Delete "${editing.title}"? This cannot be undone.`)) {
                      remove.mutate(editing.id);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              {!online && <div className={s.hint}>Editing is disabled while offline.</div>}
            </div>

            {save.isError && (
              <div className={s.ownedNote}>
                <i className="ti ti-alert-triangle" aria-hidden />
                <span>{save.error instanceof Error ? save.error.message : 'Could not save.'}</span>
              </div>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
